import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { auth } from './config';

// Admin emails that have access to admin areas
const ADMIN_EMAILS: string[] = [
  'admin@gmail.com',
  'nitin@moizwordpress.com',
  'moizwordpress1@gmail.com'
];


// Affiliate emails that should have restricted access
const AFFILIATE_EMAILS: string[] = [
  // Add affiliate emails here if they need special handling
];

// Login function
export const loginAdmin = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check if the user is an admin
    if (user.email && ADMIN_EMAILS.includes(user.email)) {
      return { success: true, user, role: 'admin' };
    } else if (user.email && AFFILIATE_EMAILS.includes(user.email)) {
      // Sign out if affiliate tries to access admin areas
      await signOut(auth);
      throw new Error('Access denied. Affiliate users cannot access admin areas.');
    } else {
      // Sign out if not admin
      await signOut(auth);
      throw new Error('Access denied. Admin privileges required.');
    }
  } catch (error: any) {
    console.error('❌ Login error:', error);
    console.error('🔍 Error details:', {
      code: error?.code || 'Unknown code',
      message: error?.message || 'Unknown error'
    });

    // Provide more specific error messages
    if (error?.code === 'auth/user-not-found') {
      throw new Error('Admin user not found. Please create the admin account first.');
    } else if (error?.code === 'auth/wrong-password') {
      throw new Error('Incorrect password. Please try again.');
    } else if (error?.code === 'auth/invalid-email') {
      throw new Error('Invalid email address.');
    } else if (error?.code === 'auth/too-many-requests') {
      throw new Error('Too many failed attempts. Please try again later.');
    }

    throw new Error(error.message || 'Login failed');
  }
};

// Create admin user (for initial setup)
export const createAdminUser = async (email: string, password: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    return { success: true, user };
  } catch (error: any) {
    console.error('❌ Error creating admin user:', error);
    console.error('🔍 Error details:', {
      code: error?.code || 'Unknown code',
      message: error?.message || 'Unknown error'
    });

    if (error?.code === 'auth/email-already-in-use') {
      throw new Error('Admin user already exists. Please try logging in instead.');
    } else if (error?.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Please use a stronger password.');
    } else if (error?.code === 'auth/invalid-email') {
      throw new Error('Invalid email address.');
    }

    throw new Error(error.message || 'Failed to create admin user');
  }
};

// Logout function
export const logoutAdmin = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    console.error('❌ Logout error:', error);
    throw new Error(error.message || 'Logout failed');
  }
};

// Check if user is admin
export const isAdmin = (user: User | null): boolean => {
  return user?.email ? ADMIN_EMAILS.includes(user.email) : false;
};

// Check if user is affiliate (for access control)
export const isAffiliate = (user: User | null): boolean => {
  return user?.email ? AFFILIATE_EMAILS.includes(user.email) : false;
};

// Check if user has admin access (admin only, not affiliates)
export const hasAdminAccess = (user: User | null): boolean => {
  if (!user?.email) return false;
  return ADMIN_EMAILS.includes(user.email) && !AFFILIATE_EMAILS.includes(user.email);
};

// Get user role
export const getUserRole = (user: User | null): 'admin' | 'affiliate' | 'user' => {
  if (!user?.email) return 'user';
  if (ADMIN_EMAILS.includes(user.email)) return 'admin';
  if (AFFILIATE_EMAILS.includes(user.email)) return 'affiliate';
  return 'user';
};

// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// Listen to auth state changes
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, (user) => {
    callback(user);
  });
};

// --- New functionality for Reviews ---

const googleProvider = new GoogleAuthProvider();

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

export const signUpUser = async (email: string, name: string, password: string) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName: name });
    return result.user;
  } catch (error) {
    console.error('Error signing up user:', error);
    throw error;
  }
};

export const signInUser = async (email: string, password: string) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing in user:', error);
    throw error;
  }
};
